/**
 * @ComponentFor Avensia.Common.Features.Product.VariationViewModel
 */
import React, { useState, useEffect, useRef } from 'react';
import { scrollPosition, scrollTo, translate } from '@polarnopyret/scope';
import connect from 'Shared/connect';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import { styled } from '@glitz/react';
import VariationViewModel from '../VariationViewModel.type';
import { ButtonHeight, ButtonTheme } from 'Shared/TextButtons';
import FeedbackButton from 'Shared/Button/Feedback';
import { Plus12x12 } from 'Shared/Icons/Plus';
import { addToCart } from 'Cart/action-creators';
import {
  H1,
  TextBlockSmall,
  Text,
  Paragraph,
  ResponsiveSize,
  ResponsiveLineHeight,
} from 'Shared/SharedComponents/atoms/Typography';
import { appendImagePreset, Preset } from 'Shared/image-sizes';
import { Check12x12 } from 'Shared/Icons/Check';
import Page from 'Shared/Page';
import { CircleFilled8x8 } from 'Shared/Icons/Circle';
import { Store12x12 } from 'Shared/Icons/Store';
import ExtendedData from 'Product/ExtendedData';
import Usp from 'AppShell/Models/Usp.type';
import RecommendationsList from 'Product/SwipeableList';
import ProductFeatureList from 'Product/ProductFeatureList';
import PreviousPurchaseList from 'Product/PreviousPurchase';
import { SelectableSizes } from 'Product/Sizes';
import { colorFromStatus } from 'Shared/utils';
import StoreModel from 'Stores/StoreModel.type';
import Reviews from 'Product/Reviews';
import ProductTags from 'Product/ProductTags';
import Price from 'Pricing/Price';
import Link from 'Shared/Link';
import { toggleWishlistItem as toggleWishlistData } from 'WishList/action-creators';
import MiniWishListViewModel from 'WishList/MiniWishListViewModel.type';
import WishListUpdateResultType from 'WishList/WishListUpdateResult.type';
import { openAccountBox } from 'SiteLayout/AccountBox/action-creators';
import ProductAccordion from 'Product/ProductAccordion';
import { GridWrap } from 'Shared/PageLayout';
import StoreAvailabilityModal from 'Product/StoreAvailabilityModal';
import SizeGuideModal from 'Product/SizeGuideModal';
import { BreadcrumbWrapper } from 'Shared/Page/PageContent';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { DesktopImageGallery } from 'Product/ImageGallery/DesktopImageGallery';
import { MobileImageGallery } from 'Product/ImageGallery/MobileImageGallery';
import * as style from 'Shared/Style';

const FlexElement = styled.div({
  display: 'flex',
});

const MainInfo = styled.div({
  width: '100%',
  ['@media ' + style.mediaUpToLarge]: {
    padding: {
      top: '24px',
      x: '12px',
      bottom: '12px',
    },
  },
  ['@media ' + style.mediaMinLarge]: {
    padding: {
      top: '0',
    },
  },
});

const SizeSelector = styled.div({
  marginTop: '48px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '32px',
  },
});

const SizeSelectorText = styled(Text, {
  marginRight: '8px',
  marginBottom: '16px',
});

const SizeGuideText = styled(Text, {
  cursor: 'pointer',
  color: style.colors.monochrome.a11ygrey,
  ':hover': {
    color: style.colors.monochrome.black,
  },
  marginBottom: '16px',
});

const AddToCart = styled.div({
  marginTop: '48px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '32px',
  },
});

const BackInventoryWrap = styled.div({
  marginTop: '16px',
  backgroundColor: style.colors.brandColors.yellow,
});

const BackInventoryText = styled(Paragraph, {
  color: style.colors.monochrome.black,
  padding: { xy: '16px' },
});

const AddToCartButton = styled(FeedbackButton, {
  width: '100%',
  marginTop: '12px',
  marginBottom: '12px',
});

const AddToCartIcon = styled(Plus12x12, {
  marginLeft: '8px',
});

const UspItem = styled.span({
  display: 'inline-flex',
  marginRight: '18px',
});

const UspItemCheck = styled(Check12x12, {
  marginRight: '6px',
});

const ProductMarkingsWrap = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
});

const ProductMarkingSplitter = styled.span({
  marginLeft: '3px',
  marginRight: '3px',
});

const BreadcrumbWrap = styled(BreadcrumbWrapper, {
  ['@media ' + style.mediaUpToLarge]: {
    padding: { bottom: '40px' },
  },
});

const TopContent = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    marginLeft: '-12px',
    marginRight: '-12px',
    borderTop: {
      style: 'solid',
      width: '2px',
      color: style.colors.monochrome.black,
    },
  },
});

const ProductDataWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    overflowY: 'auto',
    maxHeight: 'initial',
    '::-webkit-scrollbar': {
      width: '0px',
    },
    marginTop: '48px',
  },
});

const MarkingsAndReviews = styled.div({
  marginBottom: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  ['@media ' + style.mediaMinLarge]: {
    marginBottom: '12px',
    gap: '6px',
  },
});

const MainHeading = styled(H1, {
  marginBottom: '16px',
});

const VariationLink = styled(Link, {
  marginRight: '8px',
  display: 'inline-block',
  width: '48px',
  height: '64px',
  ['@media ' + style.mediaMinLarge]: {
    width: '42px',
    height: '56px',
  },
});

const VariationLinkImage = styled.img({
  borderRadius: '8px',
  objectPosition: 'center',
  objectFit: 'cover',
});

const ExtendedDataGridWrap = styled(GridWrap, {
  paddingBottom: '80px',
  ['@media ' + style.mediaMinLarge]: {
    padding: { y: '120px' },
  },
});

const FeatureBlocksGridWrap = styled(GridWrap, {
  height: 'auto',
  paddingBottom: '80px',
  ['@media ' + style.mediaUpToLarge]: {
    overflow: 'hidden',
  },
  ['@media ' + style.mediaMinLarge]: {
    paddingBottom: '120px',
  },
});

const AccordionGridWrap = styled(GridWrap, {
  height: 'auto',
});

const RecommendationsGridWrap = styled(GridWrap, {
  height: 'auto',
  padding: { y: '80px' },
  ['@media ' + style.mediaUpToLarge]: {
    overflow: 'hidden',
  },
  ['@media ' + style.mediaMinLarge]: {
    padding: { y: '120px' },
  },
});

const ItemInventory = styled(FlexElement, {
  gap: '18px',
});

const WebInventoryInfoWrap = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
});

const SelectedStoreInfoWrap = styled.span({
  display: 'inline-flex',
  gap: '4px',
  alignItems: 'center',
});

const SelectedStoreInfoCity = styled(Text, {
  color: style.colors.monochrome.a11ygrey,
});

const StoreInfoWrap = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const StoreInfoText = styled(Text, {
  textDecoration: 'underline',
});

const ChangeStore = styled.span({
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'underline',
});

const ShopIcon = styled(Store12x12, {
  width: '12px',
  marginRight: '4px',
});

const StoreIcon = styled(ShopIcon, {
  color: style.colors.monochrome.black,
});

const Variation = connect(
  (state) => ({
    wishlist: state.wishlist,
    currentBreakpoint: state.currentBreakpoint,
    culture: state.appShellData.culture,
    currency: state.appShellData.market.currency,
    usps: state.appShellData.usps,
    selectedStore: state.UI?.selectedStore,
    wishlistPageLocation: state.appShellData.pages?.wishlist?.url,
    loggedIn: state.currentUser?.isLoggedIn ?? false,
  }),
  (dispatch) => ({
    toggleWishlistItem(isFavorited: boolean, code: string, colorProductCode: string, currentCategory: string) {
      return dispatch(toggleWishlistData(isFavorited, code, colorProductCode, currentCategory));
    },
    addToCart(code: string, quantity: number, ticket: string, currentCategory: string, colorProductCode: string) {
      return dispatch(
        addToCart(code, quantity, ticket, 'Produktlista', currentCategory, colorProductCode, false, true),
      );
    },
    openAccountBox() {
      dispatch(openAccountBox());
    },
  }),
)(
  (
    props: VariationViewModel & {
      addToCart: (
        code: string,
        quantity: number,
        ticket: string,
        currentCategory: string,
        colorProductCode: string,
      ) => Promise<void>;
      toggleWishlistItem: (
        isFavorited: boolean,
        code: string,
        colorProductCode: string,
        currentCategory: string,
      ) => Promise<WishListUpdateResultType>;
      openAccountBox: () => void;
      currentBreakpoint: number;
      currency: string;
      culture: string;
      usps: Usp[];
      selectedStore: StoreModel;
      wishlist: MiniWishListViewModel;

      wishlistPageLocation: string;
      loggedIn: boolean;
    },
  ) => {
    const {
      wishlist,
      currentBreakpoint,
      addToCart,
      openAccountBox,
      toggleWishlistItem,
      usps,
      labels,
      name,
      sizes,
      deliveryUsps,
      variations,
      productTitle,
      productActivities,
      preSelectedSizeCode,
      previousPurchases,
      selectedStoreStatus,
      selectedStore,
      loggedIn,
      sizeBackupStoreAvailabilities,
      barcodes
    } = props;

    useEffect(() => {
      window?.document && scrollPosition(0);
    }, []);

    const scrollToDetailsRef = useRef<HTMLDivElement>();
    const scrollToReviewsRef = useRef<HTMLDivElement>();
    const breadcrumbs = props.breadcrumbs;
    const [isFavorited, setFavourite] = useState(wishlist.items.some((item) => item === props.code));

    const toggleWishlist = async (isFavorited: boolean) => {
      const b = await toggleWishlistItem(isFavorited, props.preSelectedSizeCode, props.code, props.categoryName);
      if (b?.statusCode === 200) {
        setFavourite(!isFavorited);
      }
    };

    const toggleFavorited = (e: any): any => {
      if (loggedIn) {
        toggleWishlist(isFavorited);
      } else {
        return openAccountBox();
      }
    };

    const scrollToDetails = () => {
      if (scrollToDetailsRef) {
        scrollTo(scrollToDetailsRef.current.offsetTop - 50, 500);
      }
    };

    const scrollToReviews = () => {
      if (contextDisplayData.numOfReviews > 0) {
        setExpandedAccordion('reviews');
        if (scrollToReviewsRef) {
          scrollTo(scrollToReviewsRef.current.offsetTop - 100, 500);
        }
      }
    };

    const isMobile = functionIsCompact(currentBreakpoint);
    const [selectedSize, setSelectedSize] = useState(
      sizes.find((s) => s.code === preSelectedSizeCode) ?? sizes.find((s) => s.inStock),
    );

    const [sizeGuideModalIsShown, setSizeGuideModalIsShown] = useState<boolean>(false);
    const [storeAvailabilityModalIsShown, setStoreAvailabilityModalIsShown] = useState<boolean>(false);
    const [expandedAccordion, setExpandedAccordion] = useState<string>();

    const { code, ticket, price, centralStockAvailability, inStock, color } = selectedSize;
    const contextDisplayData = props.productContextDisplayData.find(
      (x) => x.code === (props.compositeItem ? selectedSize.colorProductCode : props.code),
    );

    useEffect(() => {
      if (document.head) {
        let schema = document.getElementById('pdp-schema') as HTMLScriptElement;
        if (!schema) {
          schema = document.createElement('script');
          schema.type = 'application/ld+json';
          schema.id = 'pdp-schema';
          document.head.appendChild(schema);
        }

        const aggregateRating =
          contextDisplayData.rating > 0
            ? {
              '@type': 'AggregateRating',
              ratingValue: contextDisplayData.rating,
              reviewCount: contextDisplayData.numOfReviews,
            }
            : undefined;

        const schemaContent = {
          '@context': 'https://schema.org/',
          '@type': 'product',
          name: name,
          description: contextDisplayData.description?.intro,
          color: selectedSize.color,
          sku: selectedSize.code,
          gtin: selectedSize.barCode,
          mpn: selectedSize.itemNumber,
          image:
            window.location.protocol +
            '//' +
            window.location.host +
            contextDisplayData.resources?.find((x) => x.type === 'IMAGE')?.imageUrl,
          brand: {
            '@type': 'Brand',
            name: 'Polarn O. Pyret',
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: props.currency,
            price: selectedSize.price.sellingPrice?.toString(),
            itemCondition: 'https://schema.org/NewCondition',
            availability: selectedSize.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: props.url,
          },
          aggregateRating,
        };

        schema.innerHTML = JSON.stringify(schemaContent);
      }
    }, [selectedSize]);

    const sentFromBackupStore = sizeBackupStoreAvailabilities
      ? sizeBackupStoreAvailabilities[selectedSize.code]
      : false;

    const availableOnline = centralStockAvailability > 0;
    const hasUsps = (usps?.length ?? 0) > 0;
    const headerOffset = hasUsps ? 140 : 108;
    const gotSizeInformation = !!props.sizeInformation;
    const itemCount = contextDisplayData.markings?.length ?? 0;
    const productMarkings = itemCount > 0 && (
      <ProductMarkingsWrap>
        {contextDisplayData.markings.map((marking, index) => (
          <>
            <Text key={index} upperCase>
              {marking}
            </Text>
            {itemCount > 1 && index + 1 < itemCount ? <ProductMarkingSplitter>/</ProductMarkingSplitter> : ''}
          </>
        ))}
      </ProductMarkingsWrap>
    );

    return (
      <Page>
        <BreadcrumbWrap isCompact={isMobile} isProductPage={true} breadcrumbs={breadcrumbs}>
          <TopContent
            className={createGridClasses(12, 11, createOffsetGridClassLarge(2) + ` ${!isMobile && 'grid subgrid-11'}`)}
          >
            {isMobile && (
              <MobileImageGallery
                resources={contextDisplayData.resources}
                tags={contextDisplayData.tags}
                isFavorited={isFavorited}
                toggleFavorited={toggleFavorited}
              />
            )}
            <ProductDataWrap className={createGridClasses(12, 5, !isMobile ? 'grid subgrid-5' : '')}>
              <MainInfo className={createGridClasses(12, 4)}>
                <MarkingsAndReviews>
                  {contextDisplayData.markings && productMarkings}
                  <Reviews
                    onClick={contextDisplayData.numOfReviews > 0 ? scrollToReviews : null}
                    rating={contextDisplayData.rating}
                    numberOfReviews={contextDisplayData.numOfReviews}
                    labels={{ reviews: labels.reviews }}
                  />
                </MarkingsAndReviews>
                <MainHeading fontSize={ResponsiveSize.D40_M32}>{name}</MainHeading>
                <Price
                  microData
                  listPrice={price.listPrice}
                  sellingPrice={price.sellingPrice}
                  comparePrice={price.comparePrice}
                  displayPriceLabels={true}
                  fontSize={isMobile ? 20 : 24}
                  fontSizePrevPrice={isMobile ? 16 : 20}
                  rowBreakMainPrice={true}
                />

                {contextDisplayData.featureHighlightTags.length !== 0 && (
                  <ProductTags
                    marginBottom={isMobile ? '48px' : '32px'}
                    marginTop={isMobile ? '24px' : '16px'}
                    tags={contextDisplayData.featureHighlightTags}
                    nrToDisplay={6}
                    readMoreLabel={labels.readDetails}
                    readMoreClick={scrollToDetails}
                  />
                )}

                <TextBlockSmall upperCase css={{ marginBottom: 16 }}>
                  {labels.color}: {color}
                </TextBlockSmall>

                {variations.length !== 0 && (
                  <div>
                    {variations.map((variation, index) => (
                      <VariationLink
                        key={index}
                        to={variation.url}
                        replaceState={true}
                        title={variation.name + ' ' + variation.color.toLowerCase()}
                      >
                        <VariationLinkImage
                          css={{
                            ...(props.code === variation.code && {
                              border: {
                                xy: { color: style.colors.monochrome.black, width: 2, style: 'solid' },
                              },
                            }),
                          }}
                          src={appendImagePreset(variation.mainImage, Preset.ItemThumb)}
                        />
                      </VariationLink>
                    ))}
                  </div>
                )}
                <SizeSelector>
                  <SizeSelectorText upperCase fontSize={ResponsiveSize.D14_M14_L130}>
                    {labels.selectSize}
                  </SizeSelectorText>
                  {gotSizeInformation && (
                    <SizeGuideText
                      upperCase
                      fontSize={ResponsiveSize.D14_M14_L130}
                      onClick={() => setSizeGuideModalIsShown(true)}
                    >
                      {labels.sizeGuide}
                    </SizeGuideText>
                  )}
                  <SelectableSizes
                    sizes={sizes}
                    selectedSizeCode={selectedSize.code}
                    setSelectedSize={(size) => {
                      if ('inStock' in size) setSelectedSize(size);
                    }}
                    placeholder={labels.selectSize}
                  />
                </SizeSelector>

                {gotSizeInformation && (
                  <SizeGuideModal
                    modalIsShown={sizeGuideModalIsShown}
                    hideModal={() => setSizeGuideModalIsShown(false)}
                    sizeInformation={props.sizeInformation}
                  />
                )}

                <AddToCart>
                  {deliveryUsps?.map((e, i) => (
                    <UspItem key={i}>
                      <UspItemCheck />
                      <Text upperCase fontSize={ResponsiveSize.D12_M12}>
                        {e}
                      </Text>
                    </UspItem>
                  ))}
                  <AddToCartButton
                    onClick={() =>
                      !inStock || props.comingSoon ? null : addToCart(code, 1, ticket, props.categoryName, props.code)
                    }
                    theme={ButtonTheme.BlackToWhite}
                    disabled={!inStock || props.comingSoon}
                    iconAfter={<AddToCartIcon />}
                    successText={props.labels.addedToCart}
                    failedText={props.labels.addToCartFailed}
                    height={ButtonHeight.Huge}
                  >
                    {props.comingSoon ? translate('/site/product/comingsoon') : labels.addToCart}
                  </AddToCartButton>
                  <ItemInventory>
                    <WebInventoryInfoWrap>
                      <CircleFilled8x8
                        css={{
                          color: availableOnline ? style.colors.brandColors.green : style.colors.brandColors.red,
                        }}
                      />
                      <Text>{labels.inventoryOnline}</Text>
                    </WebInventoryInfoWrap>
                    {props.showStoreInventoryStatus &&
                      (selectedStore ? (
                        <SelectedStoreInfoWrap>
                          {selectedStoreStatus && (
                            <CircleFilled8x8 css={{ color: colorFromStatus(selectedStoreStatus.availabilityStatus) }} />
                          )}
                          <Text>{selectedStore.name}</Text>
                          <SelectedStoreInfoCity>{selectedStore.city}</SelectedStoreInfoCity>
                          <ChangeStore onClick={() => setStoreAvailabilityModalIsShown(true)}>
                            <Paragraph fontSize={ResponsiveSize.D16_M16} lineHeight={ResponsiveLineHeight.D130_M130}>
                              {labels.changeStore}
                            </Paragraph>
                          </ChangeStore>
                        </SelectedStoreInfoWrap>
                      ) : (
                        <StoreInfoWrap onClick={() => setStoreAvailabilityModalIsShown(true)}>
                          <StoreIcon />
                          <StoreInfoText>{labels.inventoryInStore}</StoreInfoText>
                        </StoreInfoWrap>
                      ))}
                  </ItemInventory>
                </AddToCart>
                {sentFromBackupStore && props.backupInventoryText && (
                  <BackInventoryWrap>
                    <BackInventoryText>{props.backupInventoryText}</BackInventoryText>
                  </BackInventoryWrap>
                )}

                <StoreAvailabilityModal
                  modalIsShown={storeAvailabilityModalIsShown}
                  hideModal={() => setStoreAvailabilityModalIsShown(false)}
                  labels={props.storeAvailabilityLabels}
                />

                {previousPurchases?.length > 0 && (
                  <PreviousPurchaseList
                    products={previousPurchases}
                    isCompact={isMobile}
                    label={labels.fitsWithPreviousPurchases}
                    topMargin={48}
                  />
                )}
              </MainInfo>
            </ProductDataWrap>
            {!isMobile && (
              <styled.Div
                className={createGridClasses(12, 6, createOffsetGridClassLarge(6))}
                css={{ marginBottom: `-${headerOffset}px` }}
              >
                <DesktopImageGallery
                  resources={contextDisplayData.resources}
                  tags={contextDisplayData.tags}
                  isFavorited={isFavorited}
                  toggleFavorited={toggleFavorited}
                  headerOffset={headerOffset}
                />
              </styled.Div>
            )}
          </TopContent>
        </BreadcrumbWrap>
        <ExtendedDataGridWrap className="grid">
          <ExtendedData
            labels={{
              descriptionLabel: labels.productDetails,
              reviewsLabel: labels.reviews,
              saveToWishlistLabel: isFavorited ? labels.removeFromWishList : labels.saveToWishList,
              compareProductLabel: labels.compareWithOtherGarment,
              readMoreLabel: labels.readMore,
              readLessLabel: labels.readLess,
              manufacturingCountryLabel: labels.manufacturingCountry,
              manufacturingFactoryLabel: labels.manufacturingFactory,
              itemNumber: labels.itemNumber,
            }}
            rating={contextDisplayData.rating}
            numberOfReviews={contextDisplayData.numOfReviews}
            productTitle={productTitle}
            productTags={contextDisplayData.featureHighlightTags}
            productDescription={contextDisplayData.description}
            manufacturingInformation={contextDisplayData.manufacturingInformation}
            itemNumber={selectedSize.itemNumber}
            onSaveToWishlist={toggleFavorited}
            isFavorited={isFavorited}
            loggedIn={loggedIn}
            onCompareProduct={null}
            productActivities={productActivities}
            scrollToRef={scrollToDetailsRef}
          />
        </ExtendedDataGridWrap>

        {contextDisplayData.featureBlocks && contextDisplayData.featureBlocks.productFeatures?.length > 0 && (
          <FeatureBlocksGridWrap>
            <ProductFeatureList
              features={contextDisplayData.featureBlocks.productFeatures}
              isCompact={isMobile}
              label={props.labels.featureListHeading}
              smallLayout={true}
            />
          </FeatureBlocksGridWrap>
        )}
        <AccordionGridWrap>
          <ProductAccordion
            itemNumbers={barcodes}
            materialsAndCareLabel={props.labels.accordionMaterial}
            compositionLabel={props.labels.composition}
            compositions={contextDisplayData.compositions}
            careLabel={props.labels.careAdvice}
            careInstructions={contextDisplayData.careInstructions}
            sustainabilityLabel={props.labels.accordionSustainability}
            sustainability={props.sustainability}
            reviewsLabel={props.labels.accordionReviews}
            reviewLabels={{ ...props.reviewLabels, reviews: props.labels.reviews }}
            reviewCount={contextDisplayData.numOfReviews}
            reviewScore={contextDisplayData.rating}
            deliveryAndReturnsLabel={props.labels.accordionDeliveryAndReturns}
            deliveryAndReturns={props.deliveryAndReturns}
            isCompact={isMobile}
            expandedSection={expandedAccordion}
            scrollToSectionRef={scrollToReviewsRef}
            resetExpandedSection={() => setExpandedAccordion('')}
          />
        </AccordionGridWrap>
        {props.recommendations && props.recommendations.length > 0 && (
          <RecommendationsGridWrap className="grid">
            <div className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}>
              <RecommendationsList
                products={props.recommendations}
                isCompact={isMobile}
                showNav={!isMobile}
                alternativeNav={true}
                listHeading={props.labels.recommendationsTitle}
              />
            </div>
          </RecommendationsGridWrap>
        )}
      </Page>
    );
  },
);

export default Variation;
